import React from 'react';
import { createClient } from 'redis';

import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import Error from './_error';

import getConfig from 'next/config';
import {
  getContent,
  getContentSlug,
  getPostsByCategorySlug,
  getRelatedPosts,
} from '../lib/graphqlQuery';
import { SiteHead } from '../components/document/Head/Head';
import { IPost, IRelatedPost } from '../lib/types';
import { formatUrl } from '../lib/formatUrl';
import { CATEGORY_ID, REDIS_TTL, REDIS_TTL_ERROR } from '../lib/constant';
import { BottomNavigation, ViewMore } from '@lifepal/lifepal-design-system';
import {
  LOGIN_MENU,
  ACCOUNT_PROPS,
  INSURANCE_LINKS,
  TOOLS_PROPS,
} from '../lib/bottomNavConstant';
import { VIEW_MORE_MENU } from '../lib/viewMoreConstant';
import useWebView from '../utils/hooks/useWebView';

import Page from '../components/Template/Page/Page';
import Post from '../components/Template/Post/Post';
import FooterBanner from '../components/Banners/FooterBanner/FooterBanner';

import { log_error, log_info } from '../utils/logger';
import { isV2Post } from '../utils/verifier';
import { parseFooterExplore } from '../utils/parseFooterExplore';
import useResponsive from '../utils/hooks/useResponsive';
import useAuthenticated from '../utils/hooks/useAuthenticated';
import { parseCategories } from '../utils/parseCategories';
import { gtmNormalMenu } from '../lib/bottomNavConstant';
import { TABUNGAN_CATEGORY } from '../lib/constant';
import { ArticleType, ToolsPropsType } from '@lifepal/lifepal-design-system/build/components/ViewMore';

const { REDIS_HOST, REDIS_URL } = getConfig().serverRuntimeConfig;

interface SinglePostProps {
  post: IPost;
  page;
  calculatorArticles: ArticleType[];
  relatedPosts: IRelatedPost[];
  ctaTexts: [];
  statusCode: number;
}

const SinglePost = (props: SinglePostProps) => {
  const authenticated = useAuthenticated();
  const { isDesktop } = useResponsive();
  const isWebView = useWebView();

  const router = useRouter();
  const currentURL = formatUrl(router.basePath, router.asPath);

  const { post, page, relatedPosts, calculatorArticles, ctaTexts, statusCode } = props;
  const isPage = !!page;
  const postData = page ? page : post;

  const postCategory = post?.categories ? parseCategories.getSlug(post.categories) : null;
  const isTabunganCategory = postCategory ?? TABUNGAN_CATEGORY.includes(postCategory);

  if (statusCode === 500) {
    return <Error statusCode={statusCode} />;
  }

  const setTemplate = () => {
    if (isPage && !post) {
      return <Page page={page} />;
    } else {
      return <Post post={post} relatedPosts={relatedPosts} ctaTexts={ctaTexts} />;
    }
  };

  const setMobileStickyFooter = () => {
    if (authenticated) {
      const toolsProps: ToolsPropsType = {
        title: TOOLS_PROPS.title,
        subtitle: TOOLS_PROPS.subtitle,
        calculatorArticles: calculatorArticles,
      }

      return <BottomNavigation
        activePath={router.asPath}
        isLoggedIn={true}
        normalMenu={gtmNormalMenu(parseCategories.getSlug(post.categories))} //menu when user isn't logged in
        loginMenu={LOGIN_MENU}
        accountProps={ACCOUNT_PROPS} //props for Account section in normal menu
        insuranceLinks={INSURANCE_LINKS}
        toolsProps={toolsProps}
        exploreProps={parseFooterExplore(post.categories, relatedPosts)}
      />
    } else if (isTabunganCategory) return <FooterBanner slug={post.slug} banner={post.footerBanner} />;
    else return '';
  }

  const setStickyComponent = () => {
    if (post && !isDesktop && !isWebView && authenticated) {
      return (
        <ViewMore
          menu={VIEW_MORE_MENU}
          insuranceLinks={INSURANCE_LINKS}
          toolsProps={{
            title: TOOLS_PROPS.title,
            subtitle: TOOLS_PROPS.subtitle,
            calculatorArticles: calculatorArticles,
          }}
          exploreProps={parseFooterExplore(post.categories, relatedPosts)}
        />
      );
    }

  }

  return (
    <>
      <SiteHead title={postData.title} url={currentURL} post={postData} />
      {setTemplate()}
      {setStickyComponent()}
      {!isDesktop && !isWebView && setMobileStickyFooter()}
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { params } = context;
  let data: any;
  let relatedPosts: [];
  let calculatorArticles: [];

  if (typeof params.slug === 'string' && params.slug.indexOf(' ') >= 0) {
    return {
      redirect: {
        destination: '/',
        statusCode: 301,
      },
    };
  }

  log_info('getContent', 'param', params.slug);
  if (REDIS_HOST) {
    const redisKey = `post:${params.slug}`;
    const redis = createClient({ url: REDIS_URL });
    redis.on('error', (err) => log_error('Redis Create client error on getContent', 'error', err));

    await redis.connect();
    const value = await redis.get(redisKey);

    if (!value) {
      const { data: dataContent, status, errors } = await getContent(params.slug);
      data = dataContent;
      
      if (status !== 200 || errors) {        
        log_error('getContent', 'error', errors);
      } else {
        await redis.setEx(redisKey, REDIS_TTL, JSON.stringify(dataContent)); //1 hour
        log_info('getContent', 'response', `Key ${redisKey} stored in redis`);
      }
    } else {
      data = JSON.parse(value);
      log_info('getContent', 'response', `Key ${redisKey} fetched redis`);
    }

    await redis.quit();
  } else {
    const { data: dataContent } = await getContent(params.slug);
    data = dataContent;
    log_error(`getContent: Redis option`, 'error', 'Redis is not available');
  }
  log_info('getContent', 'response', data);

  if (data.post && data.post.categories && data.post.categories.nodes) {
    const { post } = data;
    const categoryId =
      data.post.categories.nodes.length > 0
        ? data.post.categories.nodes[0].databaseId
        : CATEGORY_ID.ASURANSI;

    const excludeId = post && post.databaseId;

    const response = await getRelatedPosts(categoryId, excludeId);
    relatedPosts = response && response.posts && response.posts.nodes ? response.posts.nodes : [];
  }

  if (data.post) {
    const responseCalculatorPosts = await getPostsByCategorySlug('kalkulator', 10);
    calculatorArticles =
      responseCalculatorPosts &&
        responseCalculatorPosts.posts &&
        responseCalculatorPosts.posts.nodes
        ? responseCalculatorPosts.posts.nodes
        : [];
  }

  return {
    notFound: !data.post && !data.page && data.status !== 500,
    props: {
      post: data.post,
      page: data.page,
      relatedPosts: relatedPosts ? relatedPosts : null,
      calculatorArticles: calculatorArticles ? calculatorArticles : null,
      ctaTexts: data.ctaTexts ? data.ctaTexts : null,
      statusCode: data.status,
    },
    revalidate: 2 * 60, // cache API response for 2 minutes
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { data } = await getContentSlug();
  log_info('getContentSlug', 'response', data);
  let paths = [];

  if (data && data.posts && data.posts.edges && data.posts.edges.length) {
    const postPaths = data.posts.edges.map(({ node }) => `/${node.slug}`);
    paths = [...paths, ...postPaths];
  }

  if (data && data.pages && data.pages.edges && data.pages.edges.length) {
    const pagePaths = data.pages.edges.map(({ node }) => `/${node.slug}`);
    paths = [...paths, ...pagePaths];
  }

  return {
    paths: paths.length > 0 ? paths : [],
    fallback: 'blocking',
  };
};

export default SinglePost;

